import PromptDeleteAction from '@common-components/prompt-delete-action/prompt-delete-action';
import { namespace } from '@common-config/constants';
import { backendApi, MyBridgeAgent } from '@common/reducers/backend-api/backend-api';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { reportAgentEvent } from '@common/services/agent-analytics';

/**
 * What an agent row does, the same on /agents and in Quick Agents: flip it on/off,
 * and delete it after ONE confirm dialog. One error path: a toast (the overview
 * refetch stays the source of truth, so a failed toggle simply doesn't flip).
 *
 * Spread `rowProps(agent)` onto an `AgentRow`; render `confirmDialog` once.
 */
export function useAgentRowActions() {
  const { t } = useTranslation(namespace);
  const [updateAgent] = backendApi.useUpdateMyBridgeAgentMutation();
  const [deleteAgent] = backendApi.useDeleteMyBridgeAgentMutation();
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [agentToDelete, setAgentToDelete] = useState<MyBridgeAgent | null>(null);

  const toggle = async (agent: MyBridgeAgent) => {
    setTogglingId(agent.id);
    const nextEnabled = !agent.enabled;
    try {
      await updateAgent({ id: agent.id, enabled: nextEnabled }).unwrap();
      reportAgentEvent(nextEnabled ? 'agent_activated' : 'agent_deactivated', 'claude', { agentId: agent.id });
    } catch {
      toast.error(t('teamInbox.quickAgents.toggleFailed'));
    } finally {
      setTogglingId(null);
    }
  };

  const closeDelete = async (confirmed?: boolean) => {
    const agent = agentToDelete;
    setAgentToDelete(null);
    if (!confirmed || !agent) return;
    try {
      await deleteAgent({ id: agent.id }).unwrap();
      toast.success(t('teamInbox.quickAgents.botDeleted'));
    } catch {
      toast.error(t('teamInbox.quickAgents.deleteFailed'));
    }
  };

  const rowProps = (agent: MyBridgeAgent) => ({
    onToggleEnabled: (a: MyBridgeAgent) => void toggle(a),
    toggling: togglingId === agent.id,
    onDelete: setAgentToDelete,
  });

  const confirmDialog = agentToDelete ? (
    <PromptDeleteAction title={t('myBridge.deleteConfirm', { name: agentToDelete.label })} open handleClose={(ok) => void closeDelete(ok)} />
  ) : null;

  return { rowProps, confirmDialog };
}
